import { useRef } from "react";
import {
  Type, Heading, Image as ImageIcon, Video, Link2, Mic, Quote, Minus,
  ArrowUp, ArrowDown, Trash2, Bold, Italic, Link as LinkI, Loader2, Download,
} from "lucide-react";
import { useState } from "react";
import { cmsUnfurl } from "@/lib/api";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 10);

const ADD_TYPES = [
  { type: "heading", label: "Heading", icon: Heading, data: { text: "New heading", level: 2 } },
  { type: "paragraph", label: "Text", icon: Type, data: { html: "Write something compelling…" } },
  { type: "image", label: "Image", icon: ImageIcon, data: { url: "", alt: "", align: "center", wrap: true, width: 45 } },
  { type: "video", label: "Video", icon: Video, data: { url: "" } },
  { type: "link_card", label: "Link", icon: Link2, data: { url: "", title: "", description: "", image: "" } },
  { type: "podcast", label: "Podcast", icon: Mic, data: { url: "", title: "", description: "" } },
  { type: "quote", label: "Quote", icon: Quote, data: { text: "A memorable quote" } },
  { type: "divider", label: "Divider", icon: Minus, data: {} },
];

const fieldCls = "w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand";

export default function BlockEditor({ blocks, onChange }) {
  const add = (t) => onChange([...blocks, { id: uid(), type: t.type, data: { ...t.data } }]);
  const update = (id, data) => onChange(blocks.map((b) => (b.id === id ? { ...b, data } : b)));
  const remove = (id) => onChange(blocks.filter((b) => b.id !== id));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const copy = [...blocks];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5 sticky top-0 bg-white/90 backdrop-blur py-2 z-10">
        {ADD_TYPES.map((t) => (
          <button key={t.type} onClick={() => add(t)} data-testid={`add-${t.type}`} className="flex items-center gap-1.5 text-xs font-medium border border-slate-200 rounded-lg px-3 py-2 text-slate-600 hover:border-brand hover:text-brand transition-colors">
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {blocks.length === 0 && <p className="text-slate-400 text-sm py-10 text-center border border-dashed border-slate-200 rounded-xl">Add blocks above to start building your page.</p>}
        {blocks.map((b, i) => (
          <div key={b.id} className="border border-slate-200 rounded-xl p-4 bg-slate-50/60" data-testid={`block-${b.type}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{b.type.replace("_", " ")}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} className="p-1.5 text-slate-400 hover:text-slate-700"><ArrowUp size={14} /></button>
                <button onClick={() => move(i, 1)} className="p-1.5 text-slate-400 hover:text-slate-700"><ArrowDown size={14} /></button>
                <button onClick={() => remove(b.id)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid="block-delete"><Trash2 size={14} /></button>
              </div>
            </div>
            <BlockFields block={b} update={(d) => update(b.id, d)} />
          </div>
        ))}
      </div>
    </div>
  );
}

function BlockFields({ block, update }) {
  const d = block.data || {};
  switch (block.type) {
    case "heading":
      return (
        <div className="flex gap-2">
          <select value={d.level || 2} onChange={(e) => update({ ...d, level: +e.target.value })} className="bg-white border border-slate-200 rounded-lg px-2 text-sm">
            <option value={2}>H2</option><option value={3}>H3</option>
          </select>
          <input value={d.text || ""} onChange={(e) => update({ ...d, text: e.target.value })} className={fieldCls} placeholder="Heading text" />
        </div>
      );
    case "paragraph":
      return <RichText html={d.html || ""} onChange={(html) => update({ ...d, html })} />;
    case "quote":
      return <input value={d.text || ""} onChange={(e) => update({ ...d, text: e.target.value })} className={fieldCls} placeholder="Quote text" />;
    case "divider":
      return <p className="text-xs text-slate-400">A horizontal divider.</p>;
    case "image":
      return <ImageFields d={d} update={update} />;
    case "video":
      return <div><input value={d.url || ""} onChange={(e) => update({ ...d, url: e.target.value })} className={fieldCls} placeholder="YouTube or Vimeo URL" /><p className="text-[11px] text-slate-400 mt-1">Paste a YouTube/Vimeo link — it embeds automatically.</p></div>;
    case "podcast":
      return <div className="space-y-2"><input value={d.url || ""} onChange={(e) => update({ ...d, url: e.target.value })} className={fieldCls} placeholder="Spotify/Apple/anchor podcast URL" /><input value={d.title || ""} onChange={(e) => update({ ...d, title: e.target.value })} className={fieldCls} placeholder="Episode title (optional)" /></div>;
    case "link_card":
      return <LinkFields d={d} update={update} />;
    default:
      return null;
  }
}

function RichText({ html, onChange }) {
  const ref = useRef(null);
  const [inited, setInited] = useState(false);
  const cmd = (c) => { document.execCommand(c, false, null); ref.current?.focus(); sync(); };
  const link = () => {
    const url = prompt("Link URL");
    if (url) { document.execCommand("createLink", false, url); sync(); }
  };
  const sync = () => onChange(ref.current?.innerHTML || "");
  return (
    <div>
      <div className="flex gap-1 mb-2">
        <button onClick={() => cmd("bold")} className="w-8 h-8 border border-slate-200 rounded bg-white text-slate-600 flex items-center justify-center hover:border-brand"><Bold size={14} /></button>
        <button onClick={() => cmd("italic")} className="w-8 h-8 border border-slate-200 rounded bg-white text-slate-600 flex items-center justify-center hover:border-brand"><Italic size={14} /></button>
        <button onClick={link} className="w-8 h-8 border border-slate-200 rounded bg-white text-slate-600 flex items-center justify-center hover:border-brand"><LinkI size={14} /></button>
      </div>
      <div
        ref={(el) => { ref.current = el; if (el && !inited) { el.innerHTML = html; setInited(true); } }}
        contentEditable
        suppressContentEditableWarning
        onInput={sync}
        data-testid="rich-text"
        className="min-h-[80px] bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand leading-relaxed"
      />
    </div>
  );
}

function ImageFields({ d, update }) {
  return (
    <div className="space-y-2">
      <input value={d.url || ""} onChange={(e) => update({ ...d, url: e.target.value })} className={fieldCls} placeholder="Image URL" />
      <input value={d.alt || ""} onChange={(e) => update({ ...d, alt: e.target.value })} className={fieldCls} placeholder="ALT text (accessibility & SEO)" />
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {["left", "center", "right", "full"].map((a) => (
            <button key={a} onClick={() => update({ ...d, align: a })} className={`text-xs px-2.5 py-1.5 rounded border capitalize ${d.align === a ? "border-brand bg-blue-50 text-brand" : "border-slate-200 text-slate-500"}`}>{a}</button>
          ))}
        </div>
        {(d.align === "left" || d.align === "right") && (
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input type="checkbox" checked={!!d.wrap} onChange={(e) => update({ ...d, wrap: e.target.checked })} /> Wrap text
          </label>
        )}
        {d.align !== "full" && (
          <label className="flex items-center gap-2 text-xs text-slate-600">
            Width
            <input type="range" min="25" max="100" value={d.width || 45} onChange={(e) => update({ ...d, width: +e.target.value })} />
            {d.width || 45}%
          </label>
        )}
      </div>
      {d.url && <img src={d.url} alt="" className="h-24 rounded-lg object-cover border border-slate-200" />}
    </div>
  );
}

function LinkFields({ d, update }) {
  const [loading, setLoading] = useState(false);
  const fetchMeta = async () => {
    if (!d.url) return;
    setLoading(true);
    try {
      const meta = await cmsUnfurl(d.url);
      update({ ...d, title: meta.title || d.title, description: meta.description || d.description, image: meta.image || d.image });
      toast.success("Link details fetched");
    } catch { toast.error("Couldn't fetch that link"); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input value={d.url || ""} onChange={(e) => update({ ...d, url: e.target.value })} className={fieldCls} placeholder="URL to expand" />
        <button onClick={fetchMeta} disabled={loading} data-testid="unfurl-btn" className="btn-outline rounded-lg px-3 text-sm flex items-center gap-1.5 whitespace-nowrap">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Fetch
        </button>
      </div>
      <input value={d.title || ""} onChange={(e) => update({ ...d, title: e.target.value })} className={fieldCls} placeholder="Title" />
      <textarea value={d.description || ""} onChange={(e) => update({ ...d, description: e.target.value })} className={`${fieldCls} min-h-[60px]`} placeholder="Description" />
      <input value={d.image || ""} onChange={(e) => update({ ...d, image: e.target.value })} className={fieldCls} placeholder="Image URL (optional)" />
    </div>
  );
}
